import React, { useCallback, useContext, useRef, useState } from "react";
import * as styles from "./velavu-toast.module.scss";
import VelavuToastContainer from "./velavu-toast-container";

const TOAST_TIMEOUT = 10 * 1000; // 10 seconds

/// A callback that builds an element to be used as a toast
export type VelavuToastBuilder = (resolve: () => void) => React.ReactNode;

interface VelavuToastInstance {
	id: number;
	contents: React.ReactNode;
	show: boolean;
	timeout: ReturnType<typeof setTimeout> | undefined;
}

export interface VelavuToastContextProps {
	//Pushes a toast to the stack
	pushToast: (builder: VelavuToastBuilder) => void;
}

const VelavuToastContext = React.createContext<VelavuToastContextProps>({
	pushToast() {
		throw new Error("No toast context available");
	},
});

export interface VelavuToastProviderProps {
	children: React.ReactNode;
	zIndex?: number;
}

export function VelavuToastProvider(props: VelavuToastProviderProps) {
	const [toasts, setToasts] = useState<VelavuToastInstance[]>([]);
	const nextToastID = useRef(0);

	/// Removes a toast immediately from the stack.
	const removeToast = useCallback(
		(toastID: number) => {
			setToasts((prev) => {
				//Find a matching toast
				const toastIdx = prev.findIndex(
					(toast) => toast.id === toastID,
				);
				if (toastIdx === -1) {
					return prev;
				}
				const toast = prev[toastIdx];

				//Cancel the toast timeout
				clearTimeout(toast.timeout);

				//Remove the toast from the stack
				const newToasts = [...prev];
				newToasts.splice(toastIdx, 1);
				return newToasts;
			});
		},
		[setToasts],
	);

	/// Requests a toast to animate out and be removed once its animation is done.
	const animateOutToast = useCallback(
		(toastID: number) => {
			setToasts((prev) => {
				return prev.map((toast) => {
					if (toast.id === toastID) {
						//Clear the timeout
						clearTimeout(toast.timeout);

						//Set the show flag to false to animate out the toast
						return {
							...toast,
							show: false,
							timeout: undefined,
						};
					}

					return toast;
				});
			});
		},
		[setToasts],
	);

	const pushToast = useCallback(
		(builder: VelavuToastBuilder) => {
			//Get a toast ID
			const id = nextToastID.current;
			nextToastID.current += 1;

			//Create the remove function
			const removeToast = () => animateOutToast(id);

			//Set the automatic removal timeout
			const timeout = setTimeout(removeToast, TOAST_TIMEOUT);

			//Build the component
			const node = builder(removeToast);
			const toastInstance: VelavuToastInstance = {
				id: id,
				contents: node,
				show: true,
				timeout: timeout,
			};

			//Add the toast to the stack
			setToasts((prev) => [...prev, toastInstance]);
		},
		[setToasts, animateOutToast],
	);

	return (
		<VelavuToastContext.Provider value={{ pushToast }}>
			{props.children}

			<div
				style={{ zIndex: props.zIndex }}
				className={styles.clipContainer}
			>
				<div className={styles.wrapper}>
					{toasts.map((toast) => (
						<VelavuToastContainer
							key={toast.id}
							show={toast.show}
							onAnimatedOut={() => removeToast(toast.id)}
						>
							{toast.contents}
						</VelavuToastContainer>
					))}
				</div>
			</div>
		</VelavuToastContext.Provider>
	);
}

export function useVelavuToast() {
	return useContext(VelavuToastContext);
}
